import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import QuestionCircleOutlined from '@ant-design/icons/QuestionCircleOutlined';
import App from 'antd/lib/app';
import Button from 'antd/lib/button';
import Row from 'antd/lib/row';
import Space from 'antd/lib/space';
import Alert from 'antd/lib/alert';
import Empty from 'antd/lib/empty';
import Layout from 'antd/lib/layout';
import Col from 'antd/lib/col';
import Card from 'antd/lib/card';
import Typography from 'antd/lib/typography';
import Tooltip from 'antd/lib/tooltip';
import { TablePaginationConfig } from 'antd/lib/table';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import isEmpty from 'lodash/isEmpty';
import { Divider } from 'antd/lib';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { AbilityContext } from '../../../../components/ability/can';
import { PageHeader } from '../../../../components/PageHeader';
import { SearchInput } from '../../../../components/searchInput/searchInput';
import { getColumnSortOrder } from '../../../../components/table/columnFormatHelpers';
import { WithEmptyDataTable } from '../../../../components/table/withEmptyDataTable';
import { useDocumentTitle } from '../../../../components/useDocumentTitle';
import { DEFAULT_PAGINATION } from '../../../../domain/commonConst';
import { IPaginationMeta } from '../../../../domain/IPagination';
import { toUsefulParams } from '../../../toUsefulParams';
import { usePageLocation } from '../../../usePageState';
import { compareWithLocale } from 'src/domain/compareWithLocale';
import { ViewSwitcher } from '../../../../components/viewSwitcher/ViewSwitcher';
import { getUtcFormattedTime } from '../../../../domain/date/date';
import { PageFooter } from '../../../../components/PageFooter';
import { CustomizableTableContextProvider } from '../../../../components/table/CustomizableTableContext';
import { CustomizableColumnType, CustomizableTable } from '../../../../components/table/CustomizableTable';
import { useVendorBillItemsListQuery } from '../../../../domain/vendor-bill-item/queries';
import { exportVendorBillItems } from '../../../../domain/vendor-bill-item';
import { settlementViewInterface } from '../settlementViewInterface';
import { IVendorBillItem } from '../../../../domain/vendor-bill-item/interface';
import { displayBillItemMetric } from '../../../../domain/vendor-bill-item/displayBillItemMetric';
import { InformationCard } from '../../../../components/informationCard/informationCard';
import { InformationCardTitle } from '../../../../components/informationCard/informationCardTitle';
import { MetricData } from '../../../../components/metricData/metricData';
import { numberFormatter } from '../../../../domain/common/numberFormatter';
import { useVendorBillItemsSidebar } from './sidebar/useVendorBillItemsSidebar';
import { VendorBillItemSidebar } from './sidebar/VendorBillItemSidebar';
import { SidebarGrid } from '../../../../components/Sidebar/SidebarGrid';
import { IVendorBillItemsSidebarFiltersContext, VendorBillItemSidebarFiltersContext } from './sidebar/VendorBillItemSidebarFiltersContext';
import { PaymentsBarChart } from '../../components/PaymentsBarChart';
import { SECONDARY_COLOR } from '../../../../theme';

import './vendorBillItems.css';

export interface IVendorBillItemsPageState {
    filter?: {
        period?: string;
        markets?: string | string[];
        utilities?: string | string[];
        programs?: string | string[];
        transactionIds?: string | string[];
        event?: string;
    };
    companyId?: number;
    search?: string;
    pagination?: {
        pageSize: number;
        current: number;
    };
    sorter?: {
        field: string;
        order: 'ascend' | 'descend';
    };
}

const prepareTotalMetricsData = (metrics: IVendorBillItem[]) => {
    const capacityPayment = metrics.reduce((sum: number, item: IVendorBillItem) => {
        return item.capacity_payment ? sum + item.capacity_payment : sum;
    }, 0);

    const energyPayment = metrics.reduce((sum: number, item: IVendorBillItem) => {
        return item.energy_payment ? sum + item.energy_payment : sum;
    }, 0);

    // We calculate only items with value (skip item with undefined or null)
    const performancePercentageMetrics = metrics.reduce(
        (metrics: { sum: number; count: number }, item: IVendorBillItem) => {
            return typeof item.performance_percentage === 'number'
                ? { sum: metrics.sum + item.performance_percentage, count: metrics.count + 1 }
                : metrics;
        },
        { sum: 0, count: 0 }
    );

    // We calculate only items with value (skip item with undefined or null)
    const payoutPercentageMetrics = metrics.reduce(
        (metrics: { sum: number; count: number }, item: IVendorBillItem) => {
            return typeof item.payout_percentage === 'number' ? { sum: metrics.sum + item.payout_percentage, count: metrics.count + 1 } : metrics;
        },
        { sum: 0, count: 0 }
    );

    return {
        totalPayment: Math.round(capacityPayment) + Math.round(energyPayment),
        capacityPayment: Math.round(capacityPayment),
        energyPayment: Math.round(energyPayment),
        performancePercentage: performancePercentageMetrics.count
            ? Math.round(performancePercentageMetrics.sum / performancePercentageMetrics.count)
            : 0,
        payoutPercentage: payoutPercentageMetrics.count ? Math.round(payoutPercentageMetrics.sum / payoutPercentageMetrics.count) : 0,
    };
};

export const VendorBillItems = () => {
    const { notification } = App.useApp();
    const navigate = useNavigate();
    const location = useLocation();
    const { setPageQuery, queryToState } = usePageLocation();

    const { companyId, ...queryState } = queryToState(location.search);

    // need to handle companyId separately to fill default state
    const pageState: IVendorBillItemsPageState = isEmpty(queryState) ? { companyId } : { companyId, ...queryState };

    useDocumentTitle('Settlement Data');

    const { isFiltersSidebarEnabled, sidebarCollapsed, setSidebarCollapsed, layoutPaddingLeft } = useVendorBillItemsSidebar();
    const { filters, handleDefaultFilters } = useContext<IVendorBillItemsSidebarFiltersContext>(VendorBillItemSidebarFiltersContext);

    const [isDefaultFilterApplied, setIsDefaultFilterApplied] = useState<boolean>(false);
    const [exportLoading, setExportLoading] = useState<boolean>(false);

    const ability = useContext(AbilityContext);

    const params = toUsefulParams(pageState);

    const listQuery = {
        ...(params?.filter && { filter: params?.filter }),
        ...(params?.sorter && { sorter: params?.sorter }),
        ...(params?.search && { search: params?.search }),
        company_id: params?.company_id ?? null,
        include: 'vendor_bill, vendor, settlement_file',
    };

    const {
        data,
        isError,
        error,
        isFetching,
        refetch: refetchVendorBillItemList,
    } = useVendorBillItemsListQuery(listQuery, {
        keepPreviousData: true,
        enabled: !!companyId,
    });

    const vendorBillItems = useMemo(() => data?.data || [], [data]);
    const meta = data?.meta || ({} as IPaginationMeta);

    const totalMetrics = useMemo(() => prepareTotalMetricsData(vendorBillItems), [vendorBillItems]);

    if (isError) {
        notification.error({ key: 'vendor-bill-items-fetch-error', message: error?.message || 'Cannot fetch settlement data!' });
    }

    // Apply default date range to avoid processing all items and reduce initial load time.
    // It still can be set by user
    useEffect(() => {
        if (!isEmpty(filters)) {
            setIsDefaultFilterApplied(true);
            return;
        }

        if (!isDefaultFilterApplied) {
            handleDefaultFilters({
                period: 'year_to_date',
            });
            setIsDefaultFilterApplied(true);
        }
    }, [filters, handleDefaultFilters, isDefaultFilterApplied]);

    const onViewChange = (view: string) => {
        const companyQuery = pageState.companyId ? `?companyId=${pageState.companyId}` : '';
        navigate(`${view}${companyQuery}`);
    };

    async function onTableChange(
        pagination: TablePaginationConfig,
        filter: Record<string, FilterValue | null>,
        sorter: SorterResult<IVendorBillItem> | SorterResult<IVendorBillItem>[]
    ) {
        const newFilter = {
            ...pageState.filter,
            ...filter,
        };

        const preparedParams = toUsefulParams({ pagination, sorter, filter: newFilter });

        setPageQuery({ ...pageState, ...preparedParams });
    }

    const exportVendorBillItemsAction = async () => {
        setExportLoading(true);

        try {
            await exportVendorBillItems({
                ...(params?.filter && { filter: params?.filter }),
                ...(params?.search && { search: params?.search }),
                company_id: params?.company_id ?? null,
                include: 'vendor_bill, vendor',
            });
        } catch (err: any) {
            notification.error({ key: 'vendor-bill-items-export-error', message: err.message || 'Cannot export settlement data!' });
        }

        setExportLoading(false);
    };

    const onSearch = async (value: string) => {
        setPageQuery({ ...pageState, search: value });

        // if search value is the same as in the state, then we need to refetch data and set pagination to 1 page
        if (value === (pageState.search || '')) {
            listQuery.pagination.current = DEFAULT_PAGINATION.current;

            await refetchVendorBillItemList(listQuery);
        }
    };

    const vendorBillItemColumns: CustomizableColumnType<IVendorBillItem>[] = [
        {
            key: 'season',
            title: 'Season',
            dataIndex: 'season',
            hidden: true, // Hide by default
            render: (text, record) => record.vendor_bill?.transaction_id.split('_').reverse().at(0) || '',
        },
        {
            key: 'period',
            title: 'Period',
            dataIndex: 'period',
            sorter: (a, b) => compareWithLocale(a.period, b.period),
            ...getColumnSortOrder('period', pageState.sorter),
            render: (text, record) => getUtcFormattedTime(record.period, 'MMM yyyy'), // Same format as in the Settlement file
        },
        {
            key: 'market',
            title: 'Market',
            dataIndex: 'market',
            sorter: (a, b) => compareWithLocale(a.market, b.market),
            ...getColumnSortOrder('market', pageState.sorter),
            render: (text, record) => record.market?.toUpperCase() || '',
        },
        {
            key: 'utility',
            title: 'Utility',
            dataIndex: 'utility',
            sorter: (a, b) => compareWithLocale(a.utility, b.utility),
            ...getColumnSortOrder('utility', pageState.sorter),
            render: (text, record) => record.utility,
        },
        {
            key: 'program',
            title: 'Program',
            dataIndex: 'program',
            sorter: (a, b) => compareWithLocale(a.program, b.program),
            ...getColumnSortOrder('program', pageState.sorter),
            render: (text, record) => record.program,
        },
        {
            key: 'store_number',
            title: 'Store #',
            dataIndex: 'store_number',
            sorter: (a, b) => compareWithLocale(a.store_number, b.store_number),
            ...getColumnSortOrder('store_number', pageState.sorter),
            render: (text, record) => record.store_number,
        },
        {
            key: 'event',
            title: 'Event',
            dataIndex: 'event',
            sorter: (a, b) => compareWithLocale(a.event, b.event),
            ...getColumnSortOrder('event', pageState.sorter),
            render: (text, record) => (record.event ? 'Yes' : 'No'),
        },
        {
            key: 'nomination_kw',
            title: 'Nomination KW',
            dataIndex: 'nomination_kw',
            sorter: (a, b) => compareWithLocale(a.nomination_kw, b.nomination_kw),
            ...getColumnSortOrder('nomination_kw', pageState.sorter),
            render: (text, record) => displayBillItemMetric(record.nomination_kw),
        },
        {
            key: 'performance_kw',
            title: 'Performance KW',
            dataIndex: 'performance_kw',
            sorter: (a, b) => compareWithLocale(a.performance_kw, b.performance_kw),
            ...getColumnSortOrder('performance_kw', pageState.sorter),
            render: (text, record) => displayBillItemMetric(record.performance_kw),
        },
        {
            key: 'performance_percentage',
            title: 'Performance %',
            dataIndex: 'performance_percentage',
            sorter: (a, b) => compareWithLocale(a.performance_percentage, b.performance_percentage),
            ...getColumnSortOrder('performance_percentage', pageState.sorter),
            render: (text: string, record) => displayBillItemMetric(record.performance_percentage, true),
        },
        {
            key: 'payout_kw',
            title: 'Payout KW',
            dataIndex: 'payout_kw',
            sorter: (a, b) => compareWithLocale(a.payout_kw, b.payout_kw),
            ...getColumnSortOrder('payout_kw', pageState.sorter),
            render: (text, record) => displayBillItemMetric(record.payout_kw),
        },
        {
            key: 'payout_percentage',
            title: 'Payout %',
            dataIndex: 'payout_percentage',
            sorter: (a, b) => compareWithLocale(a.payout_percentage, b.payout_percentage),
            ...getColumnSortOrder('payout_percentage', pageState.sorter),
            render: (text: string, record) => displayBillItemMetric(record.payout_percentage, true),
        },
        {
            key: 'capacity_dollar_per_kw',
            title: 'Capacity $/KW',
            dataIndex: 'capacity_dollar_per_kw',
            sorter: (a, b) => compareWithLocale(a.capacity_dollar_per_kw, b.capacity_dollar_per_kw),
            ...getColumnSortOrder('capacity_dollar_per_kw', pageState.sorter),
            render: (text: string, record) => `$ ${displayBillItemMetric(record.capacity_dollar_per_kw)}`,
        },
        {
            key: 'capacity_payment',
            title: 'Capacity Payment $',
            dataIndex: 'capacity_payment',
            sorter: (a, b) => compareWithLocale(a.capacity_payment, b.capacity_payment),
            ...getColumnSortOrder('capacity_payment', pageState.sorter),
            render: (text: string, record) => `$ ${displayBillItemMetric(record.capacity_payment)}`,
        },
        {
            key: 'energy_payment',
            title: 'Energy Payment $',
            dataIndex: 'energy_payment',
            sorter: (a, b) => compareWithLocale(a.energy_payment, b.energy_payment),
            ...getColumnSortOrder('energy_payment', pageState.sorter),
            render: (text: string, record) => `$ ${displayBillItemMetric(record.energy_payment)}`,
        },
        {
            key: 'total',
            title: 'Total $',
            dataIndex: 'total',
            sorter: (a, b) => compareWithLocale(a.total, b.total),
            ...getColumnSortOrder('total', pageState.sorter),
            render: (text: string, record) => `$ ${displayBillItemMetric(record.total)}`,
        },
        {
            key: 'payment_range',
            title: 'Payment Range',
            dataIndex: 'payment_range',
            hidden: true, // Hide by default
            render: (text: string, record) =>
                `${displayBillItemMetric(record.vendor_bill?.payment_range_low, true)} - ${displayBillItemMetric(record.vendor_bill?.payment_range_high, true)}`,
        },
        {
            key: 'payment_date',
            title: 'Payment Date',
            dataIndex: 'payment_date',
            hidden: true, // Hide by default
            render: (text, record) => getUtcFormattedTime(record.vendor_bill?.transaction_date, 'MM/dd/yyyy'), // Same format as in the Settlement file
        },

        {
            key: 'check_number',
            title: 'Check Number',
            dataIndex: 'check_number',
            hidden: true, // Hide by default
            render: (text, record) => record.vendor_bill?.check_number,
        },
        {
            key: 'vendor_bill.transaction_id',
            title: (
                <>
                    <span>Transaction Id</span>{' '}
                    <Tooltip title="Links to the file with the same name on the Files tab. Shown as plain text if there is no such file.">
                        <QuestionCircleOutlined style={{ color: SECONDARY_COLOR }} />
                    </Tooltip>
                </>
            ),
            dataIndex: 'vendor_bill.transaction_id',
            hidden: true, // Hide by default
            sorter: (a, b) => compareWithLocale(a.vendor_bill?.transaction_id, b.vendor_bill?.transaction_id),
            ...getColumnSortOrder('vendor_bill.transaction_id', pageState.sorter),
            showSorterTooltip: false,
            render: (text, record) => {
                const settlementFile = record.vendor_bill?.settlement_file;

                if (!settlementFile) {
                    return record.vendor_bill?.transaction_id;
                }

                return (
                    <a
                        href={settlementFile.previewLink || settlementFile.downloadLink}
                        target={settlementFile.previewLink ? '_blank' : '_self'}
                        rel="noreferrer"
                    >
                        {record.vendor_bill?.transaction_id}
                    </a>
                );
            },
        },
    ];

    return (
        <CustomizableTableContextProvider configKey="vendorBillItems" columns={vendorBillItemColumns}>
            <Layout className="vendor-bill-items" style={{ height: '100%', paddingLeft: layoutPaddingLeft }}>
                {isFiltersSidebarEnabled && (
                    <VendorBillItemSidebar
                        collapsed={sidebarCollapsed}
                        onCollapseChange={(collapsed: boolean) => setSidebarCollapsed(collapsed)}
                        totalRecords={meta?.total}
                        onFilterChange={() => {}}
                        gridComponent={<SidebarGrid />}
                    />
                )}
                <Layout.Content style={{ padding: '0 26px' }}>
                    <PageHeader
                        pageTitle={`Settlement Data`}
                        extra={[
                            <ViewSwitcher
                                viewOptions={settlementViewInterface(ability)}
                                key="settlement-view-switcher"
                                view={location.pathname}
                                handleViewMode={onViewChange}
                            />,
                        ]}
                        actions={[
                            <SearchInput onSearch={onSearch} defaultValue={pageState?.search} key="vendor-bill-item-search" />,
                            <Button
                                size="large"
                                key="download-csv"
                                disabled={!companyId}
                                onClick={exportVendorBillItemsAction}
                                loading={exportLoading}
                                icon={<DownloadOutlined />}
                                data-cy="export-action"
                            >
                                Download CSV
                            </Button>,
                        ]}
                    />
                    <Row justify="center" className="">
                        {!!companyId && (
                            <Space direction="horizontal" size={16} wrap className="total-metrics-sections">
                                <Space direction="horizontal" className="content-background card-style total-metrics-card-wrapper">
                                    <InformationCard
                                        classNames={['total-metrics-card']}
                                        title={<InformationCardTitle title="Total Payments" classNames={['total-metrics-card-title']} />}
                                        isLoading={isFetching}
                                    >
                                        <MetricData
                                            valueClassName={['total-metrics-card-value']}
                                            value={`$ ${numberFormatter.format(totalMetrics.totalPayment)}`}
                                        />
                                    </InformationCard>
                                    <Divider type="vertical" className="total-metrics-card-divider" />
                                    <InformationCard
                                        classNames={['total-metrics-card']}
                                        title={<InformationCardTitle title="Capacity Payments" classNames={['total-metrics-card-title']} />}
                                        isLoading={isFetching}
                                    >
                                        <MetricData
                                            valueClassName={['total-metrics-card-value']}
                                            value={`$ ${numberFormatter.format(totalMetrics.capacityPayment)}`}
                                        />
                                    </InformationCard>
                                    <Divider type="vertical" className="total-metrics-card-divider" />
                                    <InformationCard
                                        classNames={['total-metrics-card']}
                                        title={<InformationCardTitle title="Energy Payments" classNames={['total-metrics-card-title']} />}
                                        isLoading={isFetching}
                                    >
                                        <MetricData
                                            valueClassName={['total-metrics-card-value']}
                                            value={`$ ${numberFormatter.format(totalMetrics.energyPayment)}`}
                                        />
                                    </InformationCard>
                                </Space>
                                <Space direction="horizontal" className="content-background card-style total-metrics-card-wrapper">
                                    <InformationCard
                                        classNames={['total-metrics-card']}
                                        title={
                                            <InformationCardTitle
                                                title="Average Performance"
                                                classNames={['total-metrics-card-title']}
                                                tooltip="Average kW performance vs nominations for the period shown"
                                            />
                                        }
                                        isLoading={isFetching}
                                    >
                                        <MetricData
                                            valueClassName={['total-metrics-card-value']}
                                            value={`${numberFormatter.format(totalMetrics.performancePercentage)} %`}
                                        />
                                    </InformationCard>
                                    <Divider type="vertical" className="total-metrics-card-divider" />
                                    <InformationCard
                                        classNames={['total-metrics-card']}
                                        title={
                                            <InformationCardTitle
                                                title="Average Payout"
                                                classNames={['total-metrics-card-title']}
                                                tooltip="Average $ earnings vs nomination expected for the period shown"
                                            />
                                        }
                                        isLoading={isFetching}
                                    >
                                        <MetricData
                                            valueClassName={['total-metrics-card-value']}
                                            value={`${numberFormatter.format(totalMetrics.payoutPercentage)} %`}
                                        />
                                    </InformationCard>
                                </Space>
                            </Space>
                        )}
                    </Row>

                    {!!companyId && (
                        <>
                            <Row justify="center" style={{ paddingTop: '16px' }}>
                                <Typography.Text>
                                    The average is across whatever data is shown in the table (which is controlled by the filters).
                                </Typography.Text>
                            </Row>
                            <Row style={{ padding: '16px 0' }}>
                                <Col xs={24}>
                                    <Card className="content-background" style={{ padding: 0 }}>
                                        <PaymentsBarChart data={vendorBillItems} />
                                    </Card>
                                </Col>
                            </Row>
                        </>
                    )}
                    <Row>
                        {!!companyId ? (
                            <WithEmptyDataTable condition={isFetching}>
                                <CustomizableTable<IVendorBillItem>
                                    showHiddenColumnsCount
                                    size="small"
                                    rowKey={record =>
                                        `${record.vendor_bill_id}:${record.period}:${record.utility}:${record.program}:${record.store_number}:${record.event}`
                                    }
                                    className="vendor-bill-items-table"
                                    sticky={{ offsetHeader: 62 }}
                                    dataSource={vendorBillItems}
                                    loading={isFetching}
                                    onChange={onTableChange}
                                    meta={meta}
                                    pagination={{
                                        pageSize: pageState.pagination?.pageSize || DEFAULT_PAGINATION.pageSize,
                                        current: pageState.pagination?.current || DEFAULT_PAGINATION.current,
                                    }}
                                />
                            </WithEmptyDataTable>
                        ) : (
                            <Row justify="center" className="content-background card-style" style={{ width: '100%' }}>
                                <Space direction="vertical">
                                    <Alert showIcon message=" Please select a company." type="warning" />
                                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                </Space>
                            </Row>
                        )}
                    </Row>
                    <PageFooter />
                </Layout.Content>
            </Layout>
        </CustomizableTableContextProvider>
    );
};
